import React, {Component} from "react"
import Papa from "papaparse"
import {isEqual, chunk, uniqBy} from "lodash"

import {InjectIntl, injectIntl} from "@irati/utils/lang"
import {getErrorMessage} from "@irati/utils/lang/formatError"
import usersService from "@irati/identity/services/users"
import teamService from "@irati/identity/services/teams"
import {promiseSerialAll, waitPromise} from "@irati/utils/promise"
import adminTracking from "@irati-tracking/admin"
import {DONT_INCLUDE, getUserCSVHeaders, getUserCSVRows, csvRowToUser} from "@irati/admin/helpers/userCSV"
import {User, Team} from "@irati/types"
import authentication from "@irati/auth"
import Loading from "@irati/ui/src/Loading"
import {NewUserInput} from "@irati/types/ops"
import IconButton from "@irati/ui/src/Button/IconButton"

import SelectAnOffice, {SelectOfficeCallback} from "../../../components/Users/AddUsers/AddPeopleCSV/AddPeopleSelectAnOffice"
import AddPeopleCSV from "../../../components/Users/AddUsers/AddPeopleCSV/AddPeopleCSV"
import AddPeopleHeaders from "../../../components/Users/AddUsers/AddPeopleHeaders/AddPeopleHeaders"
import AddPeopleFailed from "../AddPeopleFailed/AddPeopleFailed"
import SuccessWithBackLink from "../../../components/SuccessWithBackLink"
import {ADD_PEOPLE_MULTIPLE} from "../../../routes/routeConfig"
import BackLink from "../../../components/Users/BackLink"

export enum InvitePeopleSteps {
  ADD_PEOPLE_SELECT_OFFICE = "add_people-selected-office",
  ADD_PEOPLE_CSV = "add_people-csv",
  ADD_PEOPLE_HEADERS = "add_people-headers",
  ADD_PEOPLE_FAILED = "add_people-failed",
  ADD_PEOPLE_SUCCESS = "add_people-success"
}

const TEAM_ERROR_MESSAGE = "admin.addMultiplePeople.teamErrorMessage"

interface Props extends InjectIntl {
  goBack: () => void,
  gotoSuccess?: () => void,
  alignLeft: boolean
}

interface State {
  currentStep: InvitePeopleSteps,
  headers: string[],
  csv: string[],
  isLoading: false,
  added: User[],
  notAdded: User[],
  teams: Team[],
  load: Promise<any> | null,
  officeId?: string
}

class AddMultiplePeople extends Component<Props, State> {
  state: State = {
    currentStep: InvitePeopleSteps.ADD_PEOPLE_SELECT_OFFICE,
    headers: [],
    csv: [],
    isLoading: false,
    added: [],
    notAdded: [],
    teams: [],
    load: null,
    officeId: undefined
  }

  componentDidMount() {
    const load = teamService.list(authentication.userTenant().uuid).then((teams) => {
      this.setState({teams})
    })
    this.setState({load})
  }

  updateHeader = (value: string, idx: number) => {
    const headers = this.state.headers
    headers.map((header, i) => {
      if (value && value !== DONT_INCLUDE) {
        if (header === value) {
          headers.splice(i, 1, "")
        }
      }
    })
    headers.splice(idx, 1, value)
    this.setState({headers})
  }

  onDrop = (acceptedFiles) => {
    const {formatMessage} = this.props
    let parsedRows = []
    Papa.parse(acceptedFiles[0], {
      header: false,
      skipEmptyLines: true,
      step: (row) => {
        if (row.data[0].find((value) => value.trim() !== "")) {
          parsedRows = [...parsedRows, row.data[0]]
        }
      },
      complete: () => {
        this.setState({
          headers: getUserCSVHeaders(parsedRows[0], formatMessage),
          csv: getUserCSVRows(parsedRows),
          currentStep: InvitePeopleSteps.ADD_PEOPLE_HEADERS
        })
      },
      error: (res) => {
        console.log(res)
      }
    })
  }

  submitUsers = () => {
    // WARNING: this should be tested again and batched
    const {headers, csv} = this.state
    const {formatMessage, goToSuccess} = this.props
    const adding = []
    this.setState({
      isLoading: true
    })

    csv.forEach((row) => {
      const {errors, user} = csvRowToUser(headers, row)
      if (errors.length === 0) {
        adding.push(user)
      } else {
        user.error = errors
        this.setState(({notAdded}) => {
          notAdded.push(user)
          return {notAdded}
        })
      }
    })

    const chunks = chunk(adding, 10)
    const funcs = chunks.map((userChunk) => () => {
      const users = uniqBy(userChunk, "email")
      return Promise.all(
        users.map((user) =>
          this.handleCreate(user).catch((err) => {
            if (err !== formatMessage({id: TEAM_ERROR_MESSAGE})) {
              throw err
            }
          })
        )
      )
    })

    return promiseSerialAll(funcs)
      .then(() => {
        const {notAdded, added} = this.state
        this.setState({
          isLoading: false
        })
        if (notAdded.length > 0) {
          throw new Error()
        }
        adminTracking.createUsers()
        if (goToSuccess) {
          goToSuccess(added.length)
        } else {
          this.showSuccess()
        }
      })
      .catch(() => {
        this.setState({currentStep: InvitePeopleSteps.ADD_PEOPLE_FAILED})
      })
  }

  showSuccess = () => {
    this.setState({currentStep: InvitePeopleSteps.ADD_PEOPLE_SUCCESS})
    setTimeout(() => {
      this.goBackToCSVStep()
    }, 10000)
  }

  removeFromNotAdded = (user: User, cb: () => void) => {
    const {notAdded} = this.state
    for (let i = 0; i < notAdded.length; i++) {
      if (isEqual(user, notAdded[i])) {
        notAdded.splice(i, 1)
        break
      }
    }
    this.setState({notAdded}, cb)
  }

  addUserToNotAdded = (user: User, error: string) => {
    user.error = [error]
    this.setState(({notAdded}) => {
      notAdded.push(user)
      return {notAdded}
    })
  }

  addUserToAdded = (user: User) => {
    this.setState(({added}) => {
      added.push(user)
      return {added}
    })
  }


  createUser = (data: User) => {
    const user: NewUserInput = {
      businessId: authentication.userTenant().uuid,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      externalUserId: data.externalUserId,
      language: authentication.userLocale()
    }

    const isAdmin = data.roles && data.roles.toLowerCase() === "admin"

    if (isAdmin) {
      return usersService.create({user}).then(async(createdUser) => {
        if (isAdmin) {
          await usersService.makeUserGlobalAdmin(createdUser.uuid)
        }
        this.addUserToAdded(createdUser)
        return createdUser
      })
    }
    // only fetching uuid for non admin users as it's all we need for create on bulk
    return usersService.createUserForBulk({user}).then((createdUser) => {
      this.addUserToAdded(createdUser)
      return createdUser
    })
  }

  handleCreate = (user: User) => {
    this.removeFromNotAdded(user)

    const {formatMessage} = this.props
    const {teams, officeId} = this.state

    const userTeamNames = user.teams?.replaceAll("“", "\"").replaceAll("”", "\"")
      .split(/, (?=(?:(?:[^"]*"){2})*[^"]*$)/) || []
      // the above regex splits on comma space pairings unless the comma space pairings are between double quotes.

    const invalidTeams: string[] = []
    user.teamIds = userTeamNames.map((teamName: string) => {
      const team = teams.find((test) => test.teamName === teamName.replaceAll("\"", ""))
      if (team) {
        return team.uuid
      } else {
        invalidTeams.push(teamName)
      }
      return null
    }).filter((t) => !!t)

    if (officeId) {
      user.teamIds.push(officeId)
    }

    if (invalidTeams.length) {
      const invalidTeamsString = invalidTeams.join(", ")
      this.addUserToNotAdded(user, formatMessage({id: TEAM_ERROR_MESSAGE}, {team: invalidTeamsString, count: invalidTeams.length}))
      return Promise.resolve()
    }

    return this.createUser(user).then(async(createdUser) => {
      await waitPromise(1000)
      const userId = createdUser.uuid
      return Promise.all(user.teamIds.map((teamId) => {
        return teamService.addUserToTeam(teamId, userId)
      }))
    }).catch((err) => {
      this.addUserToNotAdded(user, getErrorMessage(err))
    })
  }

  handleSelectOffice: SelectOfficeCallback = (officeId?: string) => this.setState({officeId, currentStep: InvitePeopleSteps.ADD_PEOPLE_CSV})

  goBackToCSVStep = () => {
    this.setState({currentStep: InvitePeopleSteps.ADD_PEOPLE_SELECT_OFFICE, notAdded: [], added: []})
  }

  render() {
    const {load, currentStep, added, notAdded, headers, csv, isLoading} = this.state
    const {goBack, gotoSuccess, formatMessage, alignLeft} = this.props

    return (
      <Loading load={load}>
        {goBack ? <IconButton onClick={goBack} icon="backArrow" /> : <BackLink />}
        {currentStep === InvitePeopleSteps.ADD_PEOPLE_SELECT_OFFICE && (
          <SelectAnOffice back={goBack} selectOffice={this.handleSelectOffice} />
        )}
        {currentStep === InvitePeopleSteps.ADD_PEOPLE_CSV &&
          <AddPeopleCSV
            alignLeft={alignLeft}
            back={goBack}
            onDrop={this.onDrop} />}
        {currentStep === InvitePeopleSteps.ADD_PEOPLE_HEADERS && (
          <AddPeopleHeaders
            alignLeft={alignLeft}
            headers={headers}
            usersLength={csv.length}
            csv={csv.slice(0, 5)}
            updateHeader={this.updateHeader}
            isLoading={isLoading}
            submitUsers={this.submitUsers}
            back={this.goBackToCSVStep} />
        )}
        {currentStep === InvitePeopleSteps.ADD_PEOPLE_FAILED && (
          <AddPeopleFailed
            headers={headers}
            notAdded={notAdded}
            added={added}
            handleCreate={this.handleCreate}
            removeUser={this.removeFromNotAdded}
            gotoSuccess={gotoSuccess || this.showSuccess}
            back={this.goBackToCSVStep} />
        )}
        {currentStep === InvitePeopleSteps.ADD_PEOPLE_SUCCESS && (
          <SuccessWithBackLink
            message={formatMessage({id: "admin.components.addPeopleSuccess.headerTwo.addedPeople"}, {uploadedCount: added.length})}
            backLink={ADD_PEOPLE_MULTIPLE} />
        )}
      </Loading>
    )
  }
}

export default injectIntl(AddMultiplePeople)
